import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { makeStyles } from 'tss-react/mui';
import { TextFieldForm, Typography } from 'web-library';
import { FormControlLabel, InputAdornment, Radio, RadioGroup } from '@mui/material';
import {Controller, UseFormReturn} from 'react-hook-form';
import { ExamFeeModelResponse } from '../../../../api-refactored/exam-configuration/usePostExamFeeListing';
import { fallbackNullNumber, getNumberFromString } from '../../../../utils/NumberUtils';

import {typographyTheme} from "web-library/theme/helper/tssHelper"
const useStyles = makeStyles()((theme) =>
({
  input: {
    '& input::-webkit-clear-button, & input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    paddingBottom: theme.spacing(3),
    width: theme.spacing(30),
  },
  title: {
    color: theme.color.text.textPrimary,
    paddingBottom: theme.spacing(3),
  },
  labelElement: {
     ...typographyTheme(theme, 'subtitle2'),
    fontWeight: 700,
    color: theme.color.text.textHeader,
  },
  requiredLabel: {
    display: 'inline-block',
    color: theme.color.state.stateImportant,
    marginLeft: theme.spacing(0.5),
  },
}));

interface GstFormSectionProps {
  formHook: UseFormReturn<any>;
  isModalOpened: boolean;
  defaultData?: ExamFeeModelResponse;
}

const GstFormSection = ({ formHook, isModalOpened, defaultData }: GstFormSectionProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { formState: { errors } } = formHook;
  const [gstPercent, setGstPercent] = useState<number | null>(null);

  useEffect(() => {
    if (isModalOpened) {
      // To reset the default value of the state every time modal is opened.
      if (defaultData) {
        setGstPercent(fallbackNullNumber(defaultData.gstPercent));
      } else {
        setGstPercent(null);
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isModalOpened]);

  const getGstInclusiveValue = (inclusive: boolean) => (inclusive ? 'Y' : 'N');

  return (
    <>
      <Typography variant="h3" className={classes.title}>
        {t('exam->>ExamFeeConfiguration.GstTitle')}
      </Typography>
      <TextFieldForm
        form={formHook}
        label={t('exam->>ExamFeeConfiguration.Gst')}
        name="gstPercent"
        placeholder={t('exam->>ExamFeeConfiguration.GstPercentPlaceHolder')}
        type="number"
        error={!!errors.gstPercent}
        helperText={errors?.gstPercent?.message?.toString()}
        inputProps={{
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        }}
        isRequired
        className={classes.input}
        value={gstPercent}
        // To limit user input to 2 decimal place
        onChange={(e) => {
          const val = getNumberFromString(e.target.value, 2);
          setGstPercent(val);
        }}
        onWheel={(event) => event.target instanceof HTMLElement && event.target.blur()}
      />

      <div className={classes.labelElement}>
        {t('exam->>ExamFeeConfiguration.GstInclusive')}
        <div className={classes.requiredLabel}>*</div>
      </div>
      <Controller
          control={formHook.control}
          name="gstInclusive"
          defaultValue="M"
          render={({field}) =>
              <RadioGroup
                  defaultValue={defaultData ? getGstInclusiveValue(defaultData.gstInclusive) : 'Y'}
                  {...field}
                  name="gstInclusive"
              >
                <FormControlLabel
                    id="radio-inclusive"
                    name="gstInclusive"
                    value="Y"
                    control={<Radio />}
                    label={t('exam->>ExamFeeConfiguration.Yes')}
                />
                <FormControlLabel
                    id="radio-exclusive"
                    name="gstInclusive"
                    value="N"
                    control={<Radio />}
                    label={t('exam->>ExamFeeConfiguration.No')}
                />
              </RadioGroup>
              }
        />
    </>
  );
};

export default GstFormSection;
